import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { convertCsvFileToJson, downloadJsonBackup } from '../utils/convertCsvToJson';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiUpload, FiDownload, FiFile, FiAlertTriangle, FiCheck } = FiIcons;

const CsvConverter = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [converting, setConverting] = useState(false);
  const [conversionResult, setConversionResult] = useState(null);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setError('');
        setConversionResult(null);
      } else {
        setError('Please select a CSV file');
        setSelectedFile(null);
      }
    }
  };

  const handleConvert = async () => {
    if (!selectedFile) {
      setError('Please select a CSV file first');
      return;
    }

    setConverting(true);
    setError('');

    try {
      const jsonBackup = await convertCsvFileToJson(selectedFile);
      setConversionResult(jsonBackup);
      
      // Auto-download the converted file
      downloadJsonBackup(jsonBackup);
    } catch (err) {
      console.error('Conversion error:', err);
      setError(`Conversion failed: ${err.message}`);
    } finally {
      setConverting(false);
    }
  };

  const handleReset = () => {
    setSelectedFile(null);
    setConverting(false);
    setConversionResult(null);
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="p-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-center space-x-4 mb-4">
          <Link 
            to="/settings" 
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            <span>Back to Settings</span>
          </Link>
        </div>
        <div className="flex items-center space-x-3">
          <SafeIcon icon={FiFile} className="w-8 h-8 text-primary-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">CSV to JSON Converter</h1>
            <p className="text-gray-600">Convert legacy CSV backups to the new JSON format</p>
          </div>
        </div>
      </motion.div>

      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6"
      >
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Convert Legacy Backups</h2>
        <p className="text-gray-600 mb-6">
          Use this tool to convert your older CSV backup files to the new JSON format that is compatible 
          with the enhanced backup and restore system. This ensures your historical data can be safely 
          restored in the updated application.
        </p>

        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-blue-600 mr-2" />
              <h4 className="text-sm font-medium text-blue-800">Important Information</h4>
            </div>
            <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
              <li>Only CSV backup files created by the previous version of this application are supported</li>
              <li>The converted JSON file will be automatically downloaded after conversion</li>
              <li>You can then use the converted file with the new "Restore from Backup" feature</li>
              <li>This conversion process does not modify your original CSV file</li>
            </ul>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
            <div className="flex flex-col items-center justify-center space-y-4">
              <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400" />
              
              <div>
                <p className="font-medium text-gray-900">Upload CSV Backup File</p>
                <p className="text-sm text-gray-500 mt-1">
                  Select a CSV backup file to convert to JSON format
                </p>
              </div>

              <div className="mt-2">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileChange} 
                  accept=".csv" 
                  className="sr-only" 
                  id="csv-upload" 
                />
                <label 
                  htmlFor="csv-upload" 
                  className="cursor-pointer px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Choose CSV File
                </label>
                
                {selectedFile && (
                  <div className="mt-3 flex items-center justify-center space-x-2">
                    <SafeIcon icon={FiFile} className="w-4 h-4 text-gray-500" />
                    <span className="text-sm text-gray-600 truncate max-w-xs">
                      {selectedFile.name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mr-2 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {conversionResult && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
              <SafeIcon icon={FiCheck} className="w-5 h-5 mr-2 flex-shrink-0" />
              <div>
                <p className="font-medium">Conversion Successful!</p>
                <p className="text-sm mt-1">
                  Your JSON backup has been downloaded. You can now use it with the restore feature.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-center space-x-4 pt-4">
            <button
              onClick={handleReset}
              className="px-6 py-3 text-gray-700 hover:text-gray-900 font-medium rounded-lg border border-gray-300 hover:border-gray-400 transition-colors"
            >
              Reset
            </button>
            
            <button
              onClick={handleConvert}
              disabled={!selectedFile || converting}
              className="flex items-center space-x-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              {converting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Converting...</span>
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDownload} className="w-4 h-4" />
                  <span>Convert to JSON</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CsvConverter;